import { z } from 'zod'

import { recordPath, recordRoute } from '@/shared/registry'

import type { LaunchGrant } from '../launch-types'

/** The player for a launch's attempt (begin or resume). */
export const scormPlayerRoute = '/launch/scorm/:publicId'

export function scormPlayerPath(launchId: string): string {
  return `/launch/scorm/${encodeURIComponent(launchId)}`
}

/** The player in review mode, for an activity under a grant. */
export const scormReviewPath = '/launch/scorm-review'

export function scormReviewUrl(activityId: string, grant: LaunchGrant): string {
  return `${scormReviewPath}?${new URLSearchParams({ activityId, ...grantFields(grant) }).toString()}`
}

/** Attempt details for admins. */
export const scormAttemptRoute = recordRoute('launch', 'scorm-attempts')

export function scormAttemptPath(attemptId: string): string {
  return recordPath('launch', 'scorm-attempts', attemptId)
}

/** The permission attempt details need. */
export const readLaunchesPermission = 'launch.launches.read'

/** The API's player calls. */
const scormApi = '/launch/v1/scorm'

// This page's origin, the only one the content host's wrapper will answer.
function originQuery(): string {
  return new URLSearchParams({ origin: window.location.origin }).toString()
}

export function sessionApi(launchId: string): string {
  return `${scormApi}/sessions/${encodeURIComponent(launchId)}?${originQuery()}`
}

export function reviewApi(query: string): string {
  return `${scormApi}/review?${query}&${originQuery()}`
}

export function runningApi(activityId: string, grant: LaunchGrant): string {
  return `${scormApi}/running?${new URLSearchParams({ activityId, ...grantFields(grant) }).toString()}`
}

export function trackingApi(attemptId: string): string {
  return `${scormApi}/attempts/${encodeURIComponent(attemptId)}/tracking`
}

/** Saves several SCOs' tracking together. */
export function trackingBatchApi(attemptId: string): string {
  return `${trackingApi(attemptId)}/batch`
}

export function startOverApi(attemptId: string): string {
  return `${scormApi}/attempts/${encodeURIComponent(attemptId)}/start-over`
}

export function scormAttemptApi(attemptId: string): string {
  return `/launch/v1/scorm-attempts/${encodeURIComponent(attemptId)}`
}

/** The review page's query: the activity and exactly one grant, as the review call takes it; `null` for anything else. */
export function reviewQueryOf(search: string): string | null {
  const params = new URLSearchParams(search)
  const activityId = params.get('activityId')
  const grants = ['enrollmentId', 'subscriptionId', 'classId'].filter((name) => (params.get(name) ?? '') !== '')
  if (activityId === null || activityId === '' || grants.length !== 1) {
    return null
  }
  const grant = grants[0]!
  return new URLSearchParams({ activityId, [grant]: params.get(grant)! }).toString()
}

function grantFields(grant: LaunchGrant): Readonly<Record<string, string>> {
  switch (grant.kind) {
    case 'enrollment':
      return { enrollmentId: grant.enrollmentId }
    case 'subscription':
      return { subscriptionId: grant.subscriptionId }
    case 'class':
      return { classId: grant.classId }
  }
}

/** A SCORM attempt's or SCO's status, as the API names it. */
export const scormStatuses = ['incomplete', 'complete', 'failed', 'passed', 'canceled', 'notAttempted', 'browsed'] as const

export type ScormStatus = (typeof scormStatuses)[number]

export interface ScormItem {
  readonly id: string
  readonly title: string
  readonly scoId: string | null
  readonly path: string | null
  readonly launchData: string | null
  readonly masteryScore: number | null
  readonly maxTimeAllowed: string | null
  readonly timeLimitAction: string | null
  /** Whether it opens a SCO, which talks to the player, rather than an asset, which never does. */
  readonly isSco: boolean
  readonly items: readonly ScormItem[]
}

const itemSchema: z.ZodType<ScormItem> = z.lazy(() =>
  z.object({
    id: z.string(),
    title: z.string(),
    isSco: z.boolean(),
    scoId: z.string().nullish().transform((value) => value ?? null),
    path: z.string().nullish().transform((value) => value ?? null),
    launchData: z.string().nullish().transform((value) => value ?? null),
    masteryScore: z.number().nullish().transform((value) => value ?? null),
    maxTimeAllowed: z.string().nullish().transform((value) => value ?? null),
    timeLimitAction: z.string().nullish().transform((value) => value ?? null),
    items: z.array(itemSchema),
  }),
)

const trackingSchema = z.object({
  scoId: z.string(),
  lessonStatus: z.string(),
  location: z.string().nullish(),
  suspendData: z.string().nullish(),
  scoreRaw: z.number().nullish(),
  scoreMin: z.number().nullish(),
  scoreMax: z.number().nullish(),
  preferredAudio: z.number().nullish(),
  preferredLanguage: z.string().nullish(),
  preferredSpeed: z.number().nullish(),
  preferredText: z.number().nullish(),
  totalTime: z.string().nullish(),
  exit: z.string().nullish(),
  comments: z.string().nullish(),
  interactions: z.array(z.record(z.string(), z.unknown())).nullish(),
  objectives: z.array(z.record(z.string(), z.unknown())).nullish(),
})

/** What one SCO tracked so far, in SCORM 1.2's own values. */
export type ScoTracking = z.infer<typeof trackingSchema>

const sessionSchema = z.object({
  launchId: z.string().nullish(),
  attemptId: z.string().nullish(),
  activityId: z.string(),
  name: z.string(),
  mode: z.enum(['normal', 'review']),
  status: z.enum(scormStatuses).nullish(),
  options: z.object({
    opensInNewWindow: z.boolean(),
    showsTableOfContents: z.boolean(),
    showsHeader: z.boolean(),
    showsCompletionMessage: z.boolean(),
    navigatesAutomatically: z.boolean(),
    stageWidth: z.number(),
    stageHeight: z.number(),
  }),
  title: z.string().nullish(),
  items: z.array(itemSchema),
  tracking: z.array(trackingSchema),
  studentId: z.string(),
  studentName: z.string(),
  contentToken: z.string(),
})

/** What the player needs to run a package. */
export type ScormSession = z.infer<typeof sessionSchema>

export function parseSession(body: unknown): ScormSession {
  return sessionSchema.parse(body)
}

const stateSchema = z.object({ attemptId: z.string(), status: z.enum(scormStatuses), score: z.number().nullish() })

/** The attempt's status once a SCO is saved. */
export type ScormAttemptState = z.infer<typeof stateSchema>

export function parseAttemptState(body: unknown): ScormAttemptState {
  return stateSchema.parse(body)
}

const runningSchema = z.object({ launchId: z.string(), attemptId: z.string() })

export function parseRunning(body: unknown): z.infer<typeof runningSchema> {
  return runningSchema.parse(body)
}

const attemptSchema = z.object({
  id: z.string(),
  userProfileId: z.string(),
  userProfileName: z.string().nullish(),
  activityId: z.string(),
  activityName: z.string().nullish(),
  courseId: z.string().nullish(),
  enrollmentId: z.string().nullish(),
  subscriptionId: z.string().nullish(),
  classId: z.string().nullish(),
  status: z.enum(scormStatuses),
  score: z.number().nullish(),
  totalSeconds: z.number(),
  startedOn: z.string(),
  endedOn: z.string().nullish(),
  lastActivityOn: z.string(),
  scos: z.array(
    z.object({
      scoId: z.string(),
      title: z.string().nullish(),
      lessonStatus: z.string(),
      location: z.string().nullish(),
      scoreRaw: z.number().nullish(),
      totalSeconds: z.number(),
      exit: z.string().nullish(),
      endedOn: z.string().nullish(),
      interactions: z.array(z.record(z.string(), z.unknown())).nullish(),
      objectives: z.array(z.record(z.string(), z.unknown())).nullish(),
    }),
  ),
})

/** A SCORM attempt as admins read it. */
export type ScormAttempt = z.infer<typeof attemptSchema>

export function parseScormAttempt(body: unknown): ScormAttempt {
  return attemptSchema.parse(body)
}

/** Every item that opens a file, in the table of contents' order. */
export function launchableItems(items: readonly ScormItem[]): readonly ScormItem[] {
  return items.flatMap((item) => [...(item.path !== null && item.scoId !== null ? [item] : []), ...launchableItems(item.items)])
}
